/** A ranked list of horizontal bars: top sites, top suppliers, share by category (ADR-0212, P7).
 *
 * **It draws only what has a value, and never squeezes a long list into a band.** Categories that
 * are `notAssessed` (or have nothing, `value <= 0`) are not rows: they are counted in one muted line
 * under the bars ("3 not assessed or not relevant"), and the caller can make that line a link with
 * `omittedLink`. At most `maxRows` (8) are drawn, 22px apart with 12px labels; the rest of a long
 * list are counted in the same line ("4 more"), so the full set lives behind the link, not in the
 * band. `showNotAssessed` brings the hatched, dashed-outline row back for a list where the absence
 * itself is the point.
 *
 * Plain props, no fetching. `value` is geometry (the bar's length against the largest drawn) and
 * `text` is the figure exactly as the caller printed it, so the list never sums or rounds. Hover or
 * focus on a row shows a tooltip and dims the others; Up and Down move between rows; a row with a
 * `receipt` opens it when the caller passes `onOpenReceipt`. The text alternative (and the card's
 * Table view) holds every item, drawn or not. See `BarRows` for the shared behaviour.
 */

import type { ReactNode } from "react";

import { BarRows } from "./BarRows";
import type { BarRow, BarSegment } from "./BarRows";
import type { ChartColour } from "./chartTokens";
import type { OpenReceipt } from "./receipt";

export const HBAR_MAX_ROWS = 8;

export interface HBarItem {
  key: string;
  label: string;
  sub?: string;
  /** Geometry only: the bar's length against the largest value. Ignored when `notAssessed`. */
  value: number;
  /** The figure as printed. */
  text: string;
  /** Not assessed / not relevant: not drawn (counted in the foot line), unless `showNotAssessed`. */
  notAssessed?: boolean;
  colour?: ChartColour;
  /** Extra words for the tooltip: a share, a grade. */
  note?: string;
  /** Where this row's receipt is. It opens only with the list's `onOpenReceipt`. */
  receipt?: string;
  /** The bar as parts of one whole, each in its scope's colour. */
  segments?: readonly BarSegment[];
}

export function HBarList({
  items,
  caption,
  unit,
  colour = "ramp1",
  onOpenReceipt,
  maxRows = HBAR_MAX_ROWS,
  showNotAssessed = false,
  omittedLink,
  testId = "hbar-list",
}: {
  items: readonly HBarItem[];
  /** What the list is: its accessible name and the table's caption. */
  caption: string;
  /** The figure column's heading in the text alternative: `tCO2e`. */
  unit: string;
  /** The bars' colour unless a row names its own. */
  colour?: ChartColour;
  onOpenReceipt?: OpenReceipt;
  /** At most this many rows are drawn. */
  maxRows?: number;
  showNotAssessed?: boolean;
  /** Makes the foot line a link: gets the sentence ("3 not assessed or not relevant"). */
  omittedLink?: (text: string) => ReactNode;
  testId?: string;
}) {
  const toRow = (item: HBarItem): BarRow => ({
    key: item.key,
    label: item.label,
    sub: item.sub,
    value: item.notAssessed ? null : item.value,
    text: item.text,
    hatched: item.notAssessed,
    colour: item.colour ?? colour,
    note: item.note,
    receipt: item.receipt,
    segments: item.segments,
  });
  const hasValue = (item: HBarItem) => !item.notAssessed && item.value > 0;
  const drawable = items.filter((item) => hasValue(item) || (showNotAssessed && item.notAssessed));
  const drawn = drawable.slice(0, maxRows);
  const skipped = items.filter((item) => !drawable.includes(item)).length;
  const more = drawable.length - drawn.length;

  const sentence = [
    skipped > 0 ? `${String(skipped)} not assessed or not relevant` : "",
    more > 0 ? `${String(more)} more` : "",
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <BarRows
      rows={drawn.map(toRow)}
      tableRows={items.map(toRow)}
      caption={caption}
      head={["Name", unit]}
      testId={testId}
      onOpenReceipt={onOpenReceipt}
      footer={sentence ? (omittedLink ? omittedLink(sentence) : sentence) : undefined}
    />
  );
}
